import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';

import Text, { TextProps } from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';

const Triangle = styled.div`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;
  top: -14px;
  right: 12px;
  &::after {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 8px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  }
`;

const ActionsButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.black};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

interface BoxProps {
  $right: number;
  $top: number;
  $zIndex?: number;
}

const Box = styled.div<BoxProps>`
  position: fixed;
  min-width: 125px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.white};
  z-index: ${({ $zIndex }) => $zIndex ?? 6};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  right: calc(100% - ${({ $right }) => $right + 12}px);
  top: ${({ $top }) => $top + 40}px;
`;

export const ActionsDropdownButton = styled(Text).attrs({
  $type: 'smallBold',
  $color: 'gray',
})`
  border-radius: 4px;
  padding: 0 12px;
  background-color: transparent;
  display: flex;
  width: 100%;
  align-items: center;
  height: 40px;
  transition:
    0.2s ease-out background-color,
    0.2s ease-out color;
  &:hover {
    background: ${({ theme }) => theme.colors.grayscale};
    color: ${({ theme }) => theme.colors.black};
  }
  svg {
    margin-right: 8px;
    max-width: calc(100% - 8px);
  }
  span {
    width: 32px;
    flex-shrink: 0;
    line-height: 0;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.gray};
    }
  }
` as StyledComponent<
  'button',
  DefaultTheme,
  TextProps,
  '$type' | '$color' | 'as'
>;

interface Props {
  className?: string;
  zIndex?: number;
  closeActionClick?: boolean;
}

const modalRoot = document.getElementById('modals-root');

const ActionsDropdown: FC<PropsWithChildren<Props>> = ({
  children,
  className,
  zIndex,
  closeActionClick = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const ref = useRef(document.createElement('div'));

  const buttonRef = useRef<HTMLButtonElement>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const domRect = buttonRef.current?.getBoundingClientRect();

  useEffect(() => {
    const callback = () => setShowDropdown(false);
    window.addEventListener('scroll', callback, true);
    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  useOnClickOutside(componentRef, () => setShowDropdown(false));

  return (
    <div>
      <>
        <ActionsButton
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown((prev) => !prev);
          }}
          ref={buttonRef}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.00049 5.99998C9.55277 5.99998 10.0005 5.55226 10.0005 4.99998C10.0005 4.44769 9.55277 3.99998 9.00049 3.99998C8.4482 3.99998 8.00049 4.44769 8.00049 4.99998C8.00049 5.55226 8.4482 5.99998 9.00049 5.99998Z"
              fill="currentColor"
            />
            <path
              d="M9.00049 9.99998C9.55277 9.99998 10.0005 9.55226 10.0005 8.99998C10.0005 8.44769 9.55277 7.99998 9.00049 7.99998C8.4482 7.99998 8.00049 8.44769 8.00049 8.99998C8.00049 9.55226 8.4482 9.99998 9.00049 9.99998Z"
              fill="currentColor"
            />
            <path
              d="M9.00049 14C9.55277 14 10.0005 13.5523 10.0005 13C10.0005 12.4477 9.55277 12 9.00049 12C8.4482 12 8.00049 12.4477 8.00049 13C8.00049 13.5523 8.4482 14 9.00049 14Z"
              fill="currentColor"
            />
          </svg>
        </ActionsButton>
        {createPortal(
          <>
            {domRect && showDropdown && (
              <Box
                $zIndex={zIndex}
                ref={componentRef}
                $right={domRect.x + domRect.width}
                $top={domRect.y + window.scrollY}
                className={className}
                onClick={() => closeActionClick && setShowDropdown(false)}
              >
                <Triangle />
                {children}
              </Box>
            )}
          </>,
          ref.current,
        )}
      </>
    </div>
  );
};

export default ActionsDropdown;
